/**
 * ChatWindow — ventana de conversación principal.
 *
 * R-PT-06: lista y reapertura de chats; muestra "de qué va" al abrir.
 * R-PT-07: streaming async (progress → answer/panel/clarify/escalated → done).
 * R-PT-08: panel multi-agente con voces separadas y atribuidas.
 * R-PT-09: sin estado autoritativo en cliente; rehidrata desde API.
 * R-PT-11: ActionPreview con aprobación explícita.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Loader } from 'lucide-react'
import { getChatHistory, sendMessage } from '../api/client.js'
import LlamadasTurno from './LlamadasTurno.jsx'
import CosteHilo from './CosteHilo.jsx'
import { useSession } from '../context/SessionContext.jsx'
import { AgentAvatar } from './AgentAvatar.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import CuadroConversacion from '../despacho/CuadroConversacion.jsx'
import { agenteLlamado, sinLlamada } from '../lib/llamada.js'
import { orquestadoraDe, textoAyuda } from '../lib/cuadro.js'
import Home from './Home.jsx'
import AnswerEvent       from './events/AnswerEvent.jsx'
import ClarifyEvent      from './events/ClarifyEvent.jsx'
import ProgressEvent     from './events/ProgressEvent.jsx'
import PanelEvent        from './events/PanelEvent.jsx'
import ActionPreviewEvent from './events/ActionPreviewEvent.jsx'
import EscalatedEvent    from './events/EscalatedEvent.jsx'
import NormaIncumplidaEvent from './events/NormaIncumplidaEvent.jsx'
import ActividadEvent from './events/ActividadEvent.jsx'

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function formatTime(isoString) {
  const d = new Date(isoString)
  // Turnos antiguos del kernel pueden venir sin created_at: sin hora.
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('es-ES', {
    hour: '2-digit', minute: '2-digit',
  })
}

// GEN-HU-029: coste del turno ("0.046", texto decimal del kernel) en formato
// español: coma decimal, 2 decimales; por debajo de un céntimo, «< 0,01 €».
function formatCoste(costeEur) {
  const n = Number(costeEur)
  if (costeEur == null || costeEur === '' || !Number.isFinite(n)) return ''
  if (n < 0.01) return '< 0,01 €'
  return `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

// ---------------------------------------------------------------------------
// Burbuja de mensaje del usuario
// ---------------------------------------------------------------------------
function UserBubble({ message }) {
  return (
    <div className="msg msg--user">
      <div className="msg__bubble msg__bubble--user">
        {message.content}
      </div>
      <span className="msg__time">{formatTime(message.timestamp)}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Burbuja de mensaje del agente
// ---------------------------------------------------------------------------
// Píldora "derivado a" (#280 SPEC §C.2): solo se pinta si el turno TRAE el
// dato de derivación. Todavía no viaja por SSE — este campo es tolerante y
// no se inventa; hasta que #281 lo defina, esto queda inerte en producción.
function DerivationPill({ derivation, agentColorFor }) {
  const fotoDe = useFotoAgente()  // GEN-AG-006: foto en vigor de la ficha
  if (!derivation?.nombre) return null
  const color = agentColorFor(derivation.id ?? derivation.nombre)
  return (
    <div className="derivation-pill">
      <span className="derivation-pill__arrow">→ Derivado a</span>
      <AgentAvatar name={derivation.nombre} avatarUrl={fotoDe(derivation)} size={18} ringColor={color} />
      <strong>{derivation.nombre}</strong>
      {derivation.proposito && (
        <span className="derivation-pill__proposito">· {derivation.proposito}</span>
      )}
    </div>
  )
}

// GEN-HU-028: el kernel no guarda `actividad` en el historial. Si el hilo se
// vuelve a pintar desde el historial dentro de la sesión (remonte del chat, cambio
// de chat y vuelta), las líneas se recuperan de esta memoria: por chat y por
// ordinal de turno (1.er mensaje del usuario, 2.º…). No sobrevive a recargar la página.
const actividadVista = new Map() // chatId -> Map<ordinal, payload[]>

function guardarActividad(chatId, ordinal, payload) {
  const porTurno = actividadVista.get(chatId) ?? new Map()
  porTurno.set(ordinal, [...(porTurno.get(ordinal) ?? []), payload])
  actividadVista.set(chatId, porTurno)
}

// Mensajes del historial + líneas de actividad de cada turno, justo tras la
// pregunta del usuario (es decir, encima de la respuesta del agente).
function conActividad(chatId, messages) {
  const porTurno = actividadVista.get(chatId)
  if (!porTurno) return messages
  let ordinal = 0
  return messages.flatMap(m => {
    if (m.role !== 'user') return [m]
    ordinal += 1
    const lineas = (porTurno.get(ordinal) ?? []).map((payload, i) => ({
      id: `act-${ordinal}-${i}`, role: 'agent', agent: { id: 'anne' },
      type: 'actividad', payload, timestamp: m.timestamp,
    }))
    return [m, ...lineas]
  })
}

function AgentBubble({ message, onReply, onDecision, resolveAgentName, resolveAgentRole, resolveAgentAvatar, agentColorFor, esAdmin = false, acumuladoEur = null }) {
  const [verLlamadas, setVerLlamadas] = useState(false)
  const agentName = resolveAgentName(message.agent)
  const agentRole = resolveAgentRole(message.agent)
  const agentPhoto = resolveAgentAvatar(message.agent)
  const voiceColor = agentColorFor(message.agent?.id ?? message.agent?.nombre ?? agentName)

  function renderEvent() {
    switch (message.type) {
      case 'answer':
        return <AnswerEvent payload={message.payload} onReply={onReply} />
      case 'clarify':
        return <ClarifyEvent payload={message.payload} onReply={onReply} />
      case 'progress':
        return <ProgressEvent payload={message.payload} />
      case 'panel':
        return <PanelEvent payload={message.payload} />
      case 'action_preview':
        return <ActionPreviewEvent payload={message.payload} onDecision={onDecision} />
      case 'escalated':
        return <EscalatedEvent payload={message.payload} />
      case 'norma_incumplida':
        return <NormaIncumplidaEvent payload={message.payload} />
      default:
        return <p style={{ color: 'var(--color-text-muted)' }}>[evento desconocido: {message.type}]</p>
    }
  }

  // GEN-HU-028: lo que hace el agente, en una línea discreta sin avatar
  if (message.type === 'actividad') {
    return (
      <div className="msg msg--agent msg--actividad">
        <ActividadEvent payload={message.payload} />
      </div>
    )
  }

  // Eventos de progreso: compactos, sin avatar
  if (message.type === 'progress') {
    return (
      <div className="msg msg--agent msg--progress">
        <ProgressEvent payload={message.payload} />
      </div>
    )
  }

  return (
    <div className="msg msg--agent">
      <div className="msg__avatar">
        <AgentAvatar name={agentName} avatarUrl={agentPhoto} size={28} ringColor={voiceColor} />
      </div>
      <div className="msg__body" style={voiceColor ? { '--voice-color': voiceColor } : undefined}>
        <span className="msg__agent-name-row">
          <span className="msg__agent-name">{agentName}</span>
          {agentRole && (
            <span
              className="agent-role-badge"
              style={voiceColor ? { '--badge-color': voiceColor } : undefined}
            >
              {agentRole}
            </span>
          )}
        </span>
        <div className={'msg__bubble msg__bubble--agent' + (voiceColor ? ' msg__bubble--voz' : '')}>
          {renderEvent()}
        </div>
        <span className="msg__time">
          {formatTime(message.timestamp)}
          {message.costeEur != null && formatCoste(message.costeEur) && (
            // GEN-MO-017: solo el Owner (admin) abre el detalle de llamadas, y solo si el turno trae message_id.
            esAdmin && message.messageId
              ? (
                <button
                  type="button"
                  className="msg__coste msg__coste--btn"
                  aria-expanded={verLlamadas}
                  title="Ver las llamadas de este turno"
                  onClick={() => setVerLlamadas(v => !v)}
                >
                  {' · '}Coste: {formatCoste(message.costeEur)}
                </button>
              )
              : <span className="msg__coste"> · Coste: {formatCoste(message.costeEur)}</span>
          )}
          {/* GEN-MO-024: lo que lleva el hilo hasta este turno (solo texto, solo admin). */}
          {esAdmin && acumuladoEur != null && formatCoste(acumuladoEur) && (
            <span className="msg__acumulado"> · Acumulado: {formatCoste(acumuladoEur)}</span>
          )}
        </span>
        {verLlamadas && esAdmin && message.messageId && (
          <LlamadasTurno messageId={message.messageId} fecha={message.timestamp} />
        )}
        <DerivationPill derivation={message.payload?.derivado_a} agentColorFor={agentColorFor} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estado de chat bloqueado por escalado
// ---------------------------------------------------------------------------
function BlockedBanner() {
  return (
    <div className="chat-blocked-banner">
      <span className="chat-blocked-banner__inner">
        <Loader className="icon-spin" size={15} strokeWidth={1.8} aria-hidden="true" /> Esperando respuesta del experto
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChatWindow
// ---------------------------------------------------------------------------
// GEN-195: motivo de la derivación (lo emite el kernel) -> texto de cabecera.
const TEXTO_MOTIVO = {
  elegido:  'lo has elegido',
  nombrado: 'lo has nombrado',
  tema:     'por el tema',
  sigue:    'sigue la conversación',
  ninguno:  'nadie más encajaba',
  llamado:  'lo has llamado con @',
}
const textoMotivo = (m) => TEXTO_MOTIVO[m] ?? null

// GEN-BUG-002: lo que ve el usuario cuando el turno falla sin respuesta del kernel.
const MENSAJE_ERROR_TURNO = 'No he podido completar tu petición. Inténtalo de nuevo; si se repite, avisa a soporte.'

// Nombre "amable" a partir de un id de agente (fallback si no está en la lista).
function titleCase(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

// GEN-EKB-029: turnos cuyo stream se soltó al salir del chat mientras el agente pensaba (chatId -> { texto, antes, desde }).
// El kernel termina el turno y lo guarda aunque el cliente se vaya; al volver se pinta el mensaje y se espera a que
// aparezca en el hilo. Vive a nivel de módulo porque el componente se desmonta al cambiar de zona.
const TURNOS_SOLTADOS = new Map()
const ESPERA_MAX_MS = 5 * 60 * 1000
const SONDEO_HILO_MS = 4000

export default function ChatWindow({ chatId, chatMeta, isDraft, onFirstSend, onTurnDone, onSourcesChange, onAgentesChange, onSelectChat, onSelectAgent, onAbrirAgente, agenteRetomado, onVerEquipo, onVerPersonas, onInvitar, onVerPerfil, mencionPedida, onMencionConsumida, envioInicial, onEnvioConsumido, onAbrirConversacion, textoPedido, onTextoConsumido, columna = false, envio, onEnvioConsumido: onEnvioColumnaConsumido, onOcupado, onNuevaConversacion }) {
  const fotoDe = useFotoAgente()  // GEN-AG-006: foto en vigor de la ficha
  const { agents, agentPalette, resolvedTheme, user, chats } = useSession()
  const [messages, setMessages]   = useState([])
  const esAdmin = user?.rol === 'admin'
  // GEN-MO-024: lo que lleva el hilo hasta cada turno = suma de los `costeEur` ya cargados, sin redondear
  // (se formatea al pintar). `versionCoste` = turnos con coste: cambia al cerrarse un turno y refresca el cuadro.
  const { acumulados, versionCoste } = useMemo(() => {
    const mapa = new Map()
    let suma = 0, n = 0
    for (const m of messages) {
      const v = Number(m.costeEur)
      if (m.role !== 'agent' || m.costeEur == null || m.costeEur === '' || !Number.isFinite(v)) continue
      suma += v; n += 1
      mapa.set(m.id, suma)
    }
    return { acumulados: mapa, versionCoste: n }
  }, [messages])
  const [streaming, setStreaming] = useState(false)
  const [blocked, setBlocked]     = useState(false)
  const [loading, setLoading]     = useState(false)

  // US-02: agente destino del hilo. Un borrador dirigido (clic en el rail) nace
  // con el destino en su metadata; al reabrir un chat, el historial lo aporta.
  // null = sin dirigir -> Anne-en-cliente enruta (US-03).
  const [threadAgentId, setThreadAgentId] = useState(chatMeta?.agente_destino ?? agenteRetomado?.id ?? null)
  // GEN-205: quien RESPONDIÓ el último turno (lo resuelve el router por
  // intención). Solo para mostrar: el destino que se envía sigue siendo
  // `threadAgentId`, así que enrutar por texto no fija al interlocutor.
  const [respondeId, setRespondeId] = useState(null)
  // GEN-195: por qué se derivó el último turno (lo dice el kernel; sale del SSE
  // y del historial). null = sin etiqueta (turno antiguo o aún sin turnos).
  const [motivo, setMotivo] = useState(null)

  // id -> nombre desde la lista de agentes del descriptor (US-01). Resuelve la
  // atribución de las burbujas del historial (que llegan con id) y la cabecera.
  const resolveAgentName = useCallback((agent) => {
    if (agent?.nombre) return agent.nombre
    const id = agent?.id ?? agent
    if (!id) return 'Anne'
    const found = agents.find(a => a.id === id)
    return found?.nombre ?? titleCase(id)
  }, [agents])

  // Rol del agente (para el badge de voz en el hilo). Se resuelve del
  // descriptor por id/nombre; sin match, sin badge (no se inventa un rol).
  const resolveAgentRole = useCallback((agent) => {
    const id = agent?.id ?? agent
    const found = agents.find(a => a.id === id || a.nombre === (agent?.nombre ?? id))
    return found?.rol ?? null
  }, [agents])

  // Foto del agente para el hilo — GEN-AG-006: la FOTO en vigor de su ficha
  // (FotosAgentes, desde agents_visibles[].avatar_url). La usan la cabecera y,
  // sin cambios en su cableado, la burbuja de cada turno (GEN-AG-009 decidirá
  // si el turno lleva la foto de la versión que respondió). Sin match en el
  // descriptor, sin id o sin foto, cae a iniciales (AgentAvatar).
  const resolveAgentAvatar = useCallback((agent) => {
    const id = agent?.id ?? agent
    const found = agents.find(a => a.id === id || a.nombre === (agent?.nombre ?? id))
    return fotoDe(found ?? (typeof id === 'string' ? { id } : null))
  }, [agents, fotoDe])

  // Interlocutor del hilo, en UNA sola referencia. Todo lo que habla de «con
  // quién hablas» —cabecera, empty-state y el aviso de IA (#497 N-1)— se
  // resuelve desde aquí con las MISMAS funciones que pintan el badge de rol.
  // Condición 5 del dictamen de Erin: aviso y badge no pueden desincronizarse
  // porque no hay dos fuentes que puedan divergir. Sin destino explícito el
  // interlocutor es la orquestadora en cliente, igual que en `onEvent` (US-03).
  const interlocutorId = respondeId ?? threadAgentId
  const interlocutor = interlocutorId
    ? { id: interlocutorId }
    : (chatMeta?.agente_nombre ? { nombre: chatMeta.agente_nombre } : { id: 'anne' })

  const threadAgentName = resolveAgentName(interlocutor)

  // US-03: distinguir "hablando con Anne (orquesta y dirige)" de "hablando con
  // un agente de línea (directo)". Data-driven por el rol del descriptor, no
  // por el nombre: sin destino explícito el interlocutor es la orquestadora;
  // con destino, se mira su rol. Así, cuando se conecte otro orquestador, el
  // indicador se comporta igual sin tocar código.
  // GEN-195: la etiqueta sale del motivo que dice el kernel, no se deduce en el
  // cliente. Un borrador dirigido desde el rail aún sin turnos es «elegido».
  const motivoEfectivo = motivo ?? (respondeId === null && threadAgentId ? 'elegido' : null)
  const etiquetaMotivo = textoMotivo(motivoEfectivo)

  const threadAgent = interlocutorId ? agents.find(a => a.id === interlocutorId) : null
  const esOrquestadora = !interlocutorId || /orquest/i.test(threadAgent?.rol ?? '')

  // Color de identidad por agente (#280): id o nombre -> color de
  // resolveAgentPalette (memoizada en SessionContext). R-PT-08: cada voz
  // siempre con su color; sin entrada en la paleta, sin anillo (fallback hash).
  const agentColorFor = useCallback((idOrName) => {
    if (!idOrName) return null
    const id = agents.find(a => a.id === idOrName || a.nombre === idOrName)?.id ?? idOrName
    return agentColorOf(agentPalette ?? {}, id, resolvedTheme)
  }, [agents, agentPalette, resolvedTheme])

  // Fuentes citadas del hilo (#280 RightRail): se derivan de los `sources`
  // que traen los `answer` ya recibidos. Solo lo que ha llegado; no se
  // inventa nada si un turno no cita fuentes.
  const sources = useMemo(() => {
    const seen = new Map()
    for (const m of messages) {
      for (const s of m.payload?.sources ?? []) {
        const key = s.ref ?? s.label
        if (key && !seen.has(key)) seen.set(key, s)
      }
    }
    return [...seen.values()]
  }, [messages])

  useEffect(() => { onSourcesChange?.(sources) }, [sources, onSourcesChange])

  // GEN-195: «En este chat» = solo los agentes que han respondido (id de cada
  // turno, en orden de aparición), no los del borrador ni Anne por defecto.
  const agentesQueRespondieron = useMemo(() => {
    const ids = []
    for (const m of messages) {
      const id = m.role === 'agent' ? m.agent?.id : null
      if (id && !ids.includes(id)) ids.push(id)
    }
    return ids
  }, [messages])
  useEffect(() => { onAgentesChange?.(agentesQueRespondieron) }, [agentesQueRespondieron, onAgentesChange])

  const bottomRef   = useRef(null)
  const cancelRef   = useRef(null)
  const turnoRef    = useRef(null)  // { texto, antes } del turno en vuelo; null si no hay
  const [esperando, setEsperando] = useState(false)  // GEN-EKB-029: volví al chat con un turno que sigue en el kernel
  // GEN-PX-004: mensaje enviado desde otra zona; se envía al montar y se consume.
  const envioPendienteRef = useRef(envioInicial ?? null)
  // Capturados al montar: la ventana se remonta por key al cambiar de chat,
  // así que no cambian durante la vida del componente (aunque isDraft mute
  // en el padre al registrarse el chat tras el primer envío).
  const isDraftRef   = useRef(isDraft)
  const firstSendRef = useRef(isDraft)
  const userTurnsRef = useRef(0)  // GEN-HU-028: turnos del usuario en el hilo

  // Cargar historial al abrir un chat existente (R-PT-06).
  // Conversación nueva (borrador): empieza vacía, sin llamada a la API.
  useEffect(() => {
    // Un borrador nace vacío (el estado ya lo está) y no se resetea: en StrictMode este
    // efecto corre dos veces y el segundo reset borraría el mensaje que GEN-PX-004 acaba de enviar.
    if (isDraftRef.current) return
    setMessages([])
    setRespondeId(null)
    setMotivo(null)
    setBlocked(false)
    setLoading(true)
    getChatHistory(chatId)
      .then(data => {
        userTurnsRef.current = data.messages.filter(m => m.role === 'user').length
        const historia = conActividad(chatId, data.messages)
        const suelto = TURNOS_SOLTADOS.get(chatId)
        if (suelto && (userTurnsRef.current > suelto.antes || Date.now() - suelto.desde > ESPERA_MAX_MS)) {
          TURNOS_SOLTADOS.delete(chatId)
        } else if (suelto) {
          setMessages([...historia, { id: `user-${suelto.desde}`, role: 'user', content: suelto.texto, timestamp: new Date(suelto.desde).toISOString() }])
          setEsperando(true)
          setStreaming(true)
        }
        if (!suelto || !TURNOS_SOLTADOS.has(chatId)) setMessages(historia)
        // US-02: el historial dice con qué agente es el hilo -> cabecera.
        if (data.agente_destino) setThreadAgentId(data.agente_destino)
        if (data.agente) setRespondeId(data.agente)
        setMotivo(data.motivo ?? null)
      })
      .catch(err => console.error('getChatHistory:', err))
      .finally(() => setLoading(false))
    return () => {
      if (turnoRef.current) TURNOS_SOLTADOS.set(chatId, { ...turnoRef.current, desde: Date.now() })
      cancelRef.current?.cancel()
    }
  }, [chatId])

  // GEN-EKB-029: mientras el turno soltado siga en el kernel se relee el hilo hasta que aparezca (o pasen 5 min).
  useEffect(() => {
    if (!esperando) return undefined
    let vivo = true
    const t = setInterval(() => {
      const suelto = TURNOS_SOLTADOS.get(chatId)
      if (!suelto) { setEsperando(false); setStreaming(false); return }
      getChatHistory(chatId).then(data => {
        if (!vivo) return
        const turnos = data.messages.filter(m => m.role === 'user').length
        if (turnos > suelto.antes || Date.now() - suelto.desde > ESPERA_MAX_MS) {
          TURNOS_SOLTADOS.delete(chatId)
          userTurnsRef.current = turnos
          setMessages(conActividad(chatId, data.messages))
          if (data.agente) setRespondeId(data.agente)
          setEsperando(false)
          setStreaming(false)
          onTurnDone?.()
        }
      }).catch(() => {})
    }, SONDEO_HILO_MS)
    return () => { vivo = false; clearInterval(t) }
  }, [esperando, chatId])  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll al último mensaje
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // `reanuda` {action_id, decision} (GEN-HU-016): retoma el turno que esperaba la
  // firma. No es un mensaje del usuario: no pinta burbuja ni toca el compositor.
  const handleSend = useCallback(async (text, reanuda) => {
    const content = reanuda ? '' : (text ?? '').trim()
    if ((!content && !reanuda) || streaming || blocked) return

    let ordinalTurno = userTurnsRef.current
    if (!reanuda) {
      // 1. Añadir el mensaje del usuario al hilo
      const userMsg = {
        id:        `user-${Date.now()}`,
        role:      'user',
        content,
        timestamp: new Date().toISOString(),
      }
      setMessages(prev => [...prev, userMsg])
      turnoRef.current = { texto: content, antes: userTurnsRef.current }
      userTurnsRef.current += 1
      ordinalTurno = userTurnsRef.current
    }
    setStreaming(true)

    // Primer mensaje de un borrador: refrescar SOLO el título en memoria del
    // padre (estado efímero de UI). US-65/R-PT-09: no se persiste nada — la
    // conversación entra en la lista cuando el kernel la incluya en
    // `descriptor.chats` del siguiente bootstrap.
    if (firstSendRef.current && !reanuda) {
      firstSendRef.current = false
      onFirstSend?.(content)
    }

    // ID del progress placeholder activo en el hilo.
    // Se muta de forma síncrona (fuera de setState) para evitar
    // condiciones de carrera con las actualizaciones asíncronas de React.
    let currentProgressId = null

    function onEvent(event) {
      if (event.type === 'done') {
        // GEN-HU-029: el coste llega al cerrar el turno; cuelga de la respuesta.
        const coste = event.payload?.coste_eur
        if (coste != null) {
          setMessages(prev => {
            const i = prev.findLastIndex(m => m.role === 'agent' && m.type !== 'actividad' && m.type !== 'progress')
            return i < 0 ? prev : prev.map((m, j) => j === i
              ? { ...m, costeEur: coste, ...(event.message_id ? { messageId: event.message_id } : {}) } : m)
          })
        }
        return
      }
      // GEN-HU-033: una llamada con @ no cambia quién lleva el hilo (cabecera);
      // la burbuja sí se atribuye al llamado (`agent` más abajo).
      const esLlamada = event.payload?.motivo === 'llamado'
      if (event.payload?.agent_id && !esLlamada) setRespondeId(event.payload.agent_id)
      if (event.payload?.agent_id && !esLlamada) setMotivo(event.payload.motivo ?? null)

      const msgBase = {
        id:        event.message_id ?? `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role:      'agent',
        // US-02: atribuir al agente del hilo (id -> nombre vía resolveAgentName).
        // Sin destino explícito, el interlocutor es Anne-en-cliente (US-03).
        // GEN-205: el kernel dice quién responde (`agent_id`, resuelto por el router).
        agent:     { id: event.payload?.agent_id ?? threadAgentId ?? 'anne' },
        type:      event.type,
        payload:   event.payload,
        timestamp: new Date().toISOString(),
      }

      if (event.type === 'actividad') {
        guardarActividad(chatId, ordinalTurno, event.payload)
        // GEN-HU-028: una línea por herramienta, se queda en el hilo. Id propio:
        // el `message_id` del turno es el de los trozos de `answer`.
        setMessages(prev => [...prev, { ...msgBase, id: `act-${event.seq ?? Date.now()}-${prev.length}` }])
      } else if (event.type === 'progress') {
        if (!currentProgressId) {
          // Primer progress del turno: añadir al hilo
          currentProgressId = msgBase.id
          setMessages(prev => [...prev, msgBase])
        } else {
          // Progress sucesivo: reemplazar el anterior en sitio
          const pid = currentProgressId
          setMessages(prev => prev.map(m => m.id === pid ? { ...msgBase, id: pid } : m))
        }
      } else if (event.type === 'answer') {
        // Los `answer` del kernel llegan por TROZOS (streaming §5, B2a):
        // mismo message_id -> concatenar sobre el bloque en curso;
        // message_id nuevo -> bloque nuevo.
        const pid = currentProgressId
        currentProgressId = null
        setMessages(prev => {
          const base = pid ? prev.filter(m => m.id !== pid) : prev
          const last = base[base.length - 1]
          if (last?.type === 'answer' && last.id === msgBase.id) {
            return [
              ...base.slice(0, -1),
              {
                ...last,
                payload: {
                  ...last.payload,
                  markdown: (last.payload?.markdown ?? '') + (event.payload?.markdown ?? ''),
                },
              },
            ]
          }
          return [...base, msgBase]
        })
      } else {
        // Evento final: quitar el progress y añadir el evento real
        const pid = currentProgressId
        currentProgressId = null
        setMessages(prev => {
          const sin_progress = pid ? prev.filter(m => m.id !== pid) : prev
          return [...sin_progress, msgBase]
        })
        if (event.type === 'escalated') setBlocked(true)
      }
    }

    function onDone() {
      turnoRef.current = null
      setStreaming(false)
      // GEN-PT-004: el turno ya está en el store -> la lista de la izquierda
      // se relee del kernel (R-PT-09), sin esperar al siguiente login.
      onTurnDone?.()
    }

    function onError(err) {
      console.error('SSE error:', err)
      turnoRef.current = null
      setStreaming(false)
      // GEN-BUG-002: nunca silencio; el fallo se dice en el hilo, no solo en consola.
      const pid = currentProgressId
      currentProgressId = null
      setMessages(prev => [
        ...(pid ? prev.filter(m => m.id !== pid) : prev),
        {
          id:        `err-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          role:      'agent',
          agent:     { id: threadAgentId ?? 'anne' },
          type:      'answer',
          payload:   { markdown: MENSAJE_ERROR_TURNO },
          timestamp: new Date().toISOString(),
        },
      ])
      // Reanudar un turno falla DESPUÉS de que la acción ya se ejecutó: el título
      // (y la lista) han cambiado aunque no haya respuesta que pintar.
      if (reanuda) onTurnDone?.()
    }

    const handle = sendMessage(chatId, content, {
      onEvent, onDone, onError,
      agenteDestino: threadAgentId ?? undefined,  // US-02: dirige el turno
      reanuda,
    })
    cancelRef.current = handle
  }, [chatId, streaming, blocked, onFirstSend, onTurnDone, threadAgentId])

  // GEN-HU-016: el usuario firmó (o rechazó) la acción que tenía el turno en pausa.
  const handleDecision = useCallback(
    (action_id, decision) => handleSend(null, { action_id, decision }),
    [handleSend],
  )

  // GEN-PX-004: el envío desde otra zona (u otra pantalla) abre este hilo con el
  // mensaje ya escrito; se manda una sola vez (StrictMode monta dos veces).
  useEffect(() => {
    const envio = envioPendienteRef.current
    if (!envio) return
    envioPendienteRef.current = null
    onEnvioConsumido?.()
    handleSend(envio.texto)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // GEN-EKB-022b: en modo columna (conversación de zona) el cuadro de escritura lo pone la zona, fuera de
  // esta ventana: cada mensaje llega en `envio` ({texto, n}) y se manda al hilo, una vez por `n`.
  const ultimoEnvioRef = useRef(null)
  useEffect(() => {
    if (!columna || !envio || ultimoEnvioRef.current === envio.n) return
    ultimoEnvioRef.current = envio.n
    // Cada envío se manda una vez: se avisa a la zona para que lo borre y un remonte no lo repita.
    onEnvioColumnaConsumido?.(envio.n)
    handleSend(envio.texto)
  }, [envio])  // eslint-disable-line react-hooks/exhaustive-deps
  // La zona deshabilita su cuadro mientras el turno está en curso o el chat bloqueado.
  const ocupado = streaming || blocked
  useEffect(() => { onOcupado?.(ocupado) }, [ocupado])  // eslint-disable-line react-hooks/exhaustive-deps

  // Envío del cuadro. En Inicio (borrador sin dirigir), «@Agente» abre la conversación
  // con ese agente; en cualquier otro caso va a este hilo (la @ la valida el kernel).
  function handleCuadroEnviar(texto) {
    if (showHome && onAbrirConversacion) {
      const ag = agenteLlamado(texto, agents)
      if (ag) { onAbrirConversacion(sinLlamada(texto), ag); return }
    }
    handleSend(texto)
  }

  // Chat vacío (nuevo)
  const isEmpty = !loading && messages.length === 0
  // «Home» (equipo + compositor): SOLO en un borrador SIN dirigir
  // (threadAgentId == null), es decir, el chat que enruta Anne. Un borrador
  // dirigido a un agente CONCRETO —incluida Anne al pulsar su tarjeta— abre su
  // hilo dirigido, no la Home. Ojo: gatear por `esOrquestadora` reabriría la
  // Home en bucle al elegir a la orquestadora (el destino sería Anne y su rol
  // encaja con /orquest/); por eso se gatea por «sin destino».
  const showHome = !columna && isEmpty && isDraftRef.current && threadAgentId == null && !envioPendienteRef.current

  const nombreUsuario = user?.display_name
  const enrutadora = orquestadoraDe(agents)?.nombre ?? 'Anne'
  const placeholderCuadro = blocked
    ? 'Esperando al experto…'
    : textoAyuda({ zona: showHome ? 'inicio' : 'conversacion', usuario: nombreUsuario, enrutadora, agente: threadAgentName })
  // GEN-BUG-009: «Nueva conversación» del cuadro abre una vacía con el MISMO agente que esta (el que responde; sin
  // destino, la orquestadora), no Inicio. Sin agente resoluble devuelve null y el padre cae a Inicio.
  const agenteDeLaConversacion = () => (interlocutorId ? agents.find(a => a.id === interlocutorId) : null) ?? orquestadoraDe(agents) ?? null
  const destinatario = showHome ? (orquestadoraDe(agents) ?? { id: 'anne', nombre: enrutadora }) : { id: interlocutorId, nombre: threadAgentName }

  return (
    <div className="chat-window">
      {showHome ? (
        <Home
          user={user}
          agents={agents}
          chats={chats}
          palette={agentPalette}
          theme={resolvedTheme}
          onSelectAgent={onAbrirAgente ?? onSelectAgent ?? (() => {})}
          onSelectChat={onSelectChat ?? (() => {})}
          onVerEquipo={onVerEquipo}
          onVerPersonas={onVerPersonas}
          onInvitar={onInvitar}
          onVerPerfil={onVerPerfil}
        />
      ) : (
      <>
      {/* Header (en columna lo pone la zona) */}
      {!columna && <div className="chat-header">
        {/* GEN-PX-011 (spec §5.6): conversación directa, sin asunto -> la cabecera es solo el agente. */}
        {chatMeta?.directa ? (
          <div className="chat-header__meta chat-header__meta--agente">
            <AgentAvatar name={threadAgentName} avatarUrl={resolveAgentAvatar({ id: interlocutorId, nombre: threadAgentName })} size={32} />
            <span className="chat-header__agente">
              <span className="chat-header__title">{threadAgentName}</span>
              {resolveAgentRole({ id: interlocutorId, nombre: threadAgentName }) && (
                <span className="chat-header__desc">{resolveAgentRole({ id: interlocutorId, nombre: threadAgentName })}</span>
              )}
            </span>
          </div>
        ) : (
        <div className="chat-header__meta">
          <span className="chat-header__title">
            {chatMeta ? chatMeta.titulo : 'Nueva conversación'}
          </span>
          {chatMeta?.de_que_va && (
            <span className="chat-header__desc">{chatMeta.de_que_va}</span>
          )}
        </div>
        )}
        {/* US-02/03: interlocutor visible del hilo (Anne por defecto). El
            sufijo distingue a la orquestadora (que enruta) del agente directo. */}
        {!chatMeta?.directa && <div
          className="chat-header__interlocutor"
          title={esOrquestadora
            ? `Hablando con ${threadAgentName}, que orquesta y dirige al agente adecuado`
            : `Hablando directamente con ${threadAgentName}`}
        >
          <AgentAvatar name={threadAgentName} avatarUrl={resolveAgentAvatar({ id: interlocutorId, nombre: threadAgentName })} size={22} />
          <span className="chat-header__interlocutor-label">
            Hablando con <strong>{threadAgentName}</strong>
            <span className="chat-header__interlocutor-role">
              {etiquetaMotivo
                ? ` · ${etiquetaMotivo}`
                : (respondeId === null && esOrquestadora ? ' · orquesta y dirige' : '')}
            </span>
          </span>
        </div>}
      </div>}

      {/* Hilo */}
      <div className="chat-thread" role="log" aria-live="polite" aria-atomic="false">
        {loading && (
          <p className="chat-loading">Cargando conversación…</p>
        )}

        {isEmpty && !loading && (
          <div className="chat-empty">
            <p className="chat-empty__text">
              {esOrquestadora
                ? `Escribe un mensaje para empezar.`
                : `Escribe un mensaje para ${threadAgentName}.`}
            </p>
            <p className="chat-empty__hint">
              {esOrquestadora
                ? `Estás hablando con ${threadAgentName}: si no eliges un agente, te dirigirá al más adecuado.`
                : `Estás hablando directamente con ${threadAgentName}.`}
            </p>
          </div>
        )}

        {messages.map(msg =>
          msg.role === 'user'
            ? <UserBubble key={msg.id} message={msg} />
            : <AgentBubble
                key={msg.id}
                message={msg}
                onReply={text => handleSend(text)}
                onDecision={handleDecision}
                resolveAgentName={resolveAgentName}
                resolveAgentRole={resolveAgentRole}
                resolveAgentAvatar={resolveAgentAvatar}
                agentColorFor={agentColorFor}
                esAdmin={esAdmin}
                acumuladoEur={acumulados.get(msg.id) ?? null}
              />
        )}

        {/* Estado de bloqueo y coste del hilo: van al final del hilo, no entre el hilo y el cuadro fijo. */}
        {blocked && <BlockedBanner />}

        {/* GEN-MO-024: coste de todo el hilo, desplegable, solo admin. */}
        {esAdmin && chatId && versionCoste > 0 && (
          <CosteHilo chatId={chatId} version={versionCoste} />
        )}

        <div ref={bottomRef} />
      </div>

      </>
      )}

      {/* GEN-PX-004: un solo cuadro, fijo en la base (Inicio y conversación). */}
      {!columna && <CuadroConversacion
        agents={agents}
        destinatario={destinatario}
        placeholder={placeholderCuadro}
        etiqueta={showHome ? 'Mensaje para tu equipo' : 'Mensaje'}
        enConversacion={!showHome}
        deshabilitado={streaming || blocked}
        mencionPedida={mencionPedida}
        onMencionConsumida={onMencionConsumida}
        textoPedido={textoPedido}
        onTextoConsumido={onTextoConsumido}
        onEnviar={handleCuadroEnviar}
        // GEN-BUG-007b: con una conversación iniciada (no en Inicio ni en un borrador vacío), junto a adjuntar.
        onNuevaConversacion={!showHome && !(isDraft && messages.length === 0) && onNuevaConversacion ? () => onNuevaConversacion(agenteDeLaConversacion()) : undefined}
        nuevaDeshabilitada={ocupado}
      />}
    </div>
  )
}
